import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Search, Printer, Download, Settings, ChevronDown } from 'lucide-react';
import { money, fmtDate } from '../money';

// OL Finance — parity batch 2 AJ-parity pages. Light readable tables (white card,
// slate text). No hardcoded account/report/txn values — every row comes from the DB;
// the only literals are UI labels and the canonical type/date filter vocabularies.
const CARD = 'bg-white text-slate-800 border-slate-200';
const PAGE_SIZE = 50;

// ── date presets (client-side; mirrors the report toolbar vocabulary) ──────────
function presetRange(preset) {
  const now = new Date();
  const y = now.getFullYear();
  const iso = (d) => d.toISOString().slice(0, 10);
  const first = (yy, mm) => iso(new Date(yy, mm, 1));
  const last = (yy, mm) => iso(new Date(yy, mm + 1, 0));
  const q = Math.floor(now.getMonth() / 3);
  switch (preset) {
    case 'this_month': return [first(y, now.getMonth()), last(y, now.getMonth())];
    case 'last_month': return [first(y, now.getMonth() - 1), last(y, now.getMonth() - 1)];
    case 'this_quarter': return [first(y, q * 3), last(y, q * 3 + 2)];
    case 'last_quarter': return [first(y, (q - 1) * 3), last(y, (q - 1) * 3 + 2)];
    case 'this_year': return [first(y, 0), last(y, 11)];
    case 'last_year': return [first(y - 1, 0), last(y - 1, 11)];
    case 'last_3_months': { const s = new Date(now); s.setMonth(s.getMonth() - 3); return [iso(s), iso(now)]; }
    case 'last_12_months': { const s = new Date(now); s.setMonth(s.getMonth() - 12); return [iso(s), iso(now)]; }
    default: return [null, null]; // all / custom handled by caller
  }
}
const DATE_PRESETS = [
  ['all', 'All dates'], ['this_month', 'This month'], ['last_month', 'Last month'],
  ['last_3_months', 'Last 3 months'], ['last_12_months', 'Last 12 months'],
  ['this_quarter', 'This quarter'], ['last_quarter', 'Last quarter'],
  ['this_year', 'This year'], ['last_year', 'Last year'], ['custom', 'Custom'],
];

function fmt(v) { return v == null ? '' : money(v); }
const cmp = (x, y) => {   // G2: case-insensitive, numeric-aware
  if (typeof x === 'number' && typeof y === 'number') return x - y;
  return String(x ?? '').localeCompare(String(y ?? ''), undefined, { sensitivity: 'base', numeric: true });
};

// Legacy two-group vendor/sales money bar (grouped cells)
function MoneyBar({ items, testid }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid={testid}>
      {items.map((it) => (
        <div key={it.key} className={`rounded-lg border p-3 ${CARD}`} data-testid={`${testid}-${it.key}`}>
          <div className="text-xs text-slate-500">{it.label}</div>
          <div className="text-lg font-semibold tabular-nums">{fmt(it.amount)}</div>
          {it.sub != null && <div className="text-xs text-slate-400">{it.sub}</div>}
        </div>
      ))}
    </div>
  );
}

// Single colored 5-segment money bar over the last 365 days (Sales & Customers)
const SEG_COLORS = ['#94a3b8', '#38bdf8', '#f59e0b', '#6366f1', '#059669'];
function MoneyBar5({ segments, testid }) {
  const total = segments.reduce((s, x) => s + Math.max(0, x.amount), 0) || 1;
  return (
    <div className={`rounded-lg border p-4 ${CARD}`} data-testid={testid}>
      <div className="flex h-2 rounded-full overflow-hidden mb-3">
        {segments.map((s, i) => <div key={s.key} style={{ width: `${(Math.max(0, s.amount) / total) * 100}%`, background: SEG_COLORS[i % SEG_COLORS.length] }} />)}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {segments.map((s, i) => (
          <div key={s.key} data-testid={`${testid}-${s.key}`}>
            <div className="flex items-center gap-1.5 text-xs text-slate-500"><span className="h-2.5 w-2.5 rounded-full" style={{ background: SEG_COLORS[i % SEG_COLORS.length] }} />{s.label}</div>
            <div className="text-lg font-semibold tabular-nums">{fmt(s.amount)}</div>
            {s.sub != null && <div className="text-xs text-slate-400">{s.sub}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// Shared light table with pager + gear (columns show/hide, rows per page, compact)
function FeedTable({ view, columns, rows, rowActions, selectable, sel, setSel, rowKey, empty, onRowClick, defaultSort }) {
  const [gear, setGear] = useState(false);
  const [compact, setCompact] = useState(false);
  const [perPage, setPerPage] = useState(PAGE_SIZE);
  const [page, setPage] = useState(0);
  const [hidden, setHidden] = useState({});
  const [openRow, setOpenRow] = useState(null);
  const [sortBy, setSortBy] = useState(defaultSort?.by || null);
  const [sortDir, setSortDir] = useState(defaultSort?.dir || 'asc');
  const cell = compact ? 'p-1.5' : 'p-2.5';
  const vis = columns.filter((c) => !hidden[c.key]);
  const sorted = useMemo(() => {
    if (!sortBy) return rows;
    const col = columns.find((c) => c.key === sortBy);
    const val = (r) => { const v = col?.sortVal ? col.sortVal(r) : r[sortBy]; return v == null ? '' : v; };
    const arr = [...rows].sort((a, b) => { const n = cmp(val(a), val(b)); return sortDir === 'asc' ? n : -n; });
    return arr;
  }, [rows, sortBy, sortDir, columns]);
  const size = perPage === 'All' ? (sorted.length || 1) : perPage;
  const pageCount = Math.max(1, Math.ceil(sorted.length / size));
  const cur = Math.min(page, pageCount - 1);
  const shown = perPage === 'All' ? sorted : sorted.slice(cur * size, cur * size + size);
  const toggleSort = (k) => { if (sortBy === k) { setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')); } else { setSortBy(k); setSortDir('asc'); } };
  useEffect(() => { setPage(0); }, [rows.length, perPage, sortBy, sortDir]);
  const exportCsv = () => {
    const esc = (s) => `"${String(s ?? '').replace(/"/g, '""')}"`;
    const head = vis.map((c) => esc(c.label)).join(',');
    const body = sorted.map((r) => vis.map((c) => esc(c.csv ? c.csv(r) : (c.money ? (r[c.key] == null ? '' : (r[c.key] / 100).toFixed(2)) : r[c.key]))).join(',')).join('\n');
    const blob = new Blob([`${head}\n${body}`], { type: 'text/csv' });
    const u = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = u; a.download = `${view}.csv`; a.click(); URL.revokeObjectURL(u);
  };
  const from = sorted.length === 0 ? 0 : (perPage === 'All' ? 1 : cur * size + 1);
  const to = perPage === 'All' ? sorted.length : Math.min(cur * size + size, sorted.length);
  return (
    <>
      <div className="flex items-center gap-2 justify-end">
        <Button variant="outline" size="icon" title="Export to CSV" onClick={exportCsv} data-testid={`${view}-export`}><Download className="h-4 w-4" /></Button>
        <Button variant="outline" size="icon" title="Print" onClick={() => window.print()} data-testid={`${view}-print`}><Printer className="h-4 w-4" /></Button>
        <div className="relative">
          <Button variant="outline" size="icon" title="Settings" onClick={() => setGear((v) => !v)} data-testid={`${view}-settings`}><Settings className="h-4 w-4" /></Button>
          {gear && (
            <div className="absolute right-0 top-11 z-40 w-60 rounded-lg border bg-white border-slate-200 shadow-xl p-3 text-sm space-y-3 text-slate-800" data-testid={`${view}-settings-menu`}>
              <div><p className="font-semibold mb-1">Columns</p>
                <div className="grid grid-cols-2 gap-1">
                  {columns.map((c) => (
                    <label key={c.key} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={!hidden[c.key]} onChange={() => setHidden((h) => ({ ...h, [c.key]: !h[c.key] }))} data-testid={`${view}-col-${c.key}`} /><span>{c.label}</span></label>))}
                </div></div>
              <label className="flex items-center justify-between"><span>Rows per page</span>
                <select className="border rounded bg-transparent px-2 py-1" value={perPage} onChange={(e) => setPerPage(e.target.value === 'All' ? 'All' : Number(e.target.value))} data-testid={`${view}-rows-per-page`}>
                  {[25, 50, 100, 'All'].map((n) => <option key={n} value={n}>{n}</option>)}</select></label>
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={compact} onChange={(e) => setCompact(e.target.checked)} /><span>Compact rows</span></label>
            </div>
          )}
        </div>
      </div>
      <div className={`rounded-lg border overflow-hidden ${CARD}`}>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-slate-500 border-b border-slate-200">
            {selectable && <th className={`${cell} w-8`}><input type="checkbox" checked={shown.length > 0 && shown.every((r) => sel[rowKey(r)])} onChange={(e) => { const c = e.target.checked; setSel((s) => { const n = { ...s }; shown.forEach((r) => { n[rowKey(r)] = c; }); return n; }); }} data-testid={`${view}-check-all`} /></th>}
            {vis.map((c) => <th key={c.key} className={`${cell} ${c.money ? 'text-right' : ''} cursor-pointer select-none hover:text-slate-700 uppercase text-xs tracking-wide`} onClick={() => toggleSort(c.key)} data-testid={`${view}-sort-${c.key}`}>{c.label}{sortBy === c.key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}</th>)}
            {rowActions && <th className={`${cell} w-28 uppercase text-xs tracking-wide`}>Action</th>}
          </tr></thead>
          <tbody>
            {shown.map((r, i) => (
              <tr key={rowKey(r)} className={`border-b border-slate-100 hover:bg-emerald-500/5 ${onRowClick ? 'cursor-pointer' : ''}`} data-testid={`${view}-row-${i}`}
                onClick={onRowClick ? (e) => { if (e.target.closest('input,button,a,select')) return; onRowClick(r); } : undefined}>
                {selectable && <td className={cell}><input type="checkbox" checked={!!sel[rowKey(r)]} onChange={(e) => setSel((s) => ({ ...s, [rowKey(r)]: e.target.checked }))} data-testid={`${view}-check-${i}`} /></td>}
                {vis.map((c) => <td key={c.key} className={`${cell} ${c.money ? 'text-right tabular-nums' : ''}`}>{c.render ? c.render(r) : (r[c.key] ?? '')}</td>)}
                {rowActions && (
                  <td className={`${cell} relative`}>
                    <button className="inline-flex items-center text-emerald-700 hover:underline" onClick={() => setOpenRow(openRow === i ? null : i)} data-testid={`${view}-action-${i}`}>{(rowActions(r)[0] || {}).label || 'Edit'}<ChevronDown className="h-4 w-4" /></button>
                    {openRow === i && (
                      <div className="absolute right-2 top-8 z-30 w-40 rounded-lg border bg-white border-slate-200 shadow-xl text-left" data-testid={`${view}-action-menu-${i}`}>
                        {rowActions(r).map((a) => <button key={a.label} className={`block w-full text-left px-3 py-2 text-sm hover:bg-emerald-600/10 ${a.danger ? 'text-rose-600' : ''}`} onClick={() => { setOpenRow(null); a.onClick(); }} data-testid={`${view}-ra-${i}-${a.label.replace(/\W/g, '')}`}>{a.label}</button>)}
                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={vis.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0)} className={`${cell} text-slate-500`} data-testid={`${view}-empty`}>{empty || 'Nothing here yet.'}</td></tr>}
          </tbody>
        </table>
        {sorted.length > 0 && (
          <div className="flex items-center justify-between px-3 py-2 text-sm border-t border-slate-200" data-testid={`${view}-pager`}>
            <span className="text-slate-500">{from} - {to} of {sorted.length} items</span>
            {perPage !== 'All' && pageCount > 1 && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={cur === 0} onClick={() => setPage(cur - 1)} data-testid={`${view}-prev`}>Previous</Button>
                <Button size="sm" variant="outline" disabled={cur >= pageCount - 1} onClick={() => setPage(cur + 1)} data-testid={`${view}-next`}>Next</Button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function Toolbar({ children }) { return <div className="flex items-center gap-2 flex-wrap">{children}</div>; }
function SearchBox({ value, onChange, testid, placeholder = 'Search' }) {
  return (
    <div className="relative max-w-xs flex-1"><Search className="h-4 w-4 absolute left-2 top-2.5 text-slate-400" />
      <input className="w-full h-9 pl-8 pr-2 rounded border border-slate-300 bg-white text-sm text-slate-800" placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} /></div>
  );
}
function Sel({ value, onChange, options, testid }) {
  return (
    <select className="h-9 rounded border border-slate-300 bg-white px-2 text-sm text-slate-800" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}
// Split button: primary action + ▾ menu
function SplitButton({ label, onPrimary, items, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative flex">
      <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-r-none" onClick={onPrimary} data-testid={testid}>{label}</Button>
      <button className="bg-emerald-700 hover:bg-emerald-800 text-white px-2 rounded-r-md border-l border-emerald-500" onClick={() => setOpen((v) => !v)} data-testid={`${testid}-caret`}><ChevronDown className="h-4 w-4" /></button>
      {open && (
        <div className="absolute right-0 top-10 z-30 w-48 rounded-lg border bg-white border-slate-200 shadow-xl" data-testid={`${testid}-menu`}>
          {items.map((a) => <button key={a.label} className="block w-full text-left px-3 py-2 text-sm hover:bg-emerald-600/10" onClick={() => { setOpen(false); a.onClick(); }} data-testid={`${testid}-item-${a.label.replace(/\W/g, '')}`}>{a.label}</button>)}
        </div>
      )}
    </div>
  );
}

const VOID_EP = { CHK: 'checks', BILL: 'bills', VENDCRED: 'bills', BILLCRED: 'bills', BILLPMT: 'bill-payments', INV: 'invoices', SALESRCPT: 'invoices', CREDMEM: 'invoices', PMT: 'payments' };

function useDateFilter(defaultPreset = 'all') {
  const [preset, setPreset] = useState(defaultPreset);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const range = useMemo(() => {
    if (preset === 'custom') return [from || null, to || null];
    if (preset === 'all') return [null, null];
    return presetRange(preset);
  }, [preset, from, to]);
  const inRange = (d) => { const [a, b] = range; return (!a || (d || '') >= a) && (!b || (d || '') <= b); };
  const control = (
    <>
      <Sel value={preset} onChange={setPreset} options={DATE_PRESETS} testid="date-preset" />
      {preset === 'custom' && <>
        <input type="date" className="h-9 rounded border border-slate-300 bg-white px-2 text-sm" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="date-from" />
        <input type="date" className="h-9 rounded border border-slate-300 bg-white px-2 text-sm" value={to} onChange={(e) => setTo(e.target.value)} data-testid="date-to" />
      </>}
    </>
  );
  return { inRange, control };
}

// ── (E2) EXPENSE TRANSACTIONS ──────────────────────────────────────────────────
export function ExpenseTransactionsPage({ base, go }) {
  const [data, setData] = useState({ rows: [] });
  const [q, setQ] = useState('');
  const [typeF, setTypeF] = useState('All');
  const [statusF, setStatusF] = useState('All');
  const [sel, setSel] = useState({});
  const df = useDateFilter('last_12_months');
  const load = () => axios.get(`${base}/accounting/expense-transactions`).then((r) => setData(r.data || { rows: [] })).catch(() => setData({ rows: [] }));
  useEffect(() => { load(); }, [base]);
  const rows = data.rows || [];
  const types = useMemo(() => ['All', ...Array.from(new Set(rows.map((r) => r.type)))], [rows]);
  const statuses = useMemo(() => ['All', ...Array.from(new Set(rows.map((r) => r.status).filter(Boolean)))], [rows]);
  const filtered = rows.filter((r) => (typeF === 'All' || r.type === typeF)
    && (statusF === 'All' || r.status === statusF) && df.inRange(r.date)
    && (!q || [r.payee, r.category, String(r.num), r.type].some((x) => String(x || '').toLowerCase().includes(q.toLowerCase()))));
  const doVoid = async (r) => { const ep = VOID_EP[r.code] || 'journal'; if (!window.confirm(`Void this ${r.type}?`)) return; await axios.post(`${base}/${ep}/${r.txn_id}/void`, {}).catch(() => {}); load(); };
  return (
    <div className="space-y-3 text-slate-800" data-testid="expense-txns-page">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xl font-semibold">Expense transactions</h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => go('banking', 'print')} data-testid="expense-txns-print-checks">Print Checks</Button>
          <SplitButton label="New transaction" testid="expense-txns-new" onPrimary={() => go('form', 'expense')}
            items={[{ label: 'Expense', onClick: () => go('form', 'expense') }, { label: 'Check', onClick: () => go('form', 'check') }, { label: 'Bill', onClick: () => go('form', 'bill') }, { label: 'Pay bills', onClick: () => go('form', 'paybills') }]} />
        </div>
      </div>
      <Toolbar>
        <SearchBox value={q} onChange={setQ} testid="expense-txns-search" placeholder="Search payee, category, no." />
        <Sel value={typeF} onChange={setTypeF} options={types.map((t) => [t, t])} testid="expense-txns-type" />
        <Sel value={statusF} onChange={setStatusF} options={statuses.map((s) => [s, s])} testid="expense-txns-status" />
        {df.control}
        {Object.values(sel).some(Boolean) && <Button variant="outline" onClick={() => window.print()} data-testid="expense-txns-batch-print">Print selected ({Object.values(sel).filter(Boolean).length})</Button>}
      </Toolbar>
      <FeedTable view="expense-txns" rows={filtered} selectable sel={sel} setSel={setSel} rowKey={(r) => r.txn_id}
        empty="No expenses found — Try to change some filters to see more results."
        columns={[
          { key: 'date', label: 'Date', render: (r) => fmtDate(r.date) },
          { key: 'type', label: 'Type' }, { key: 'num', label: 'No.' },
          { key: 'payee', label: 'Payee' }, { key: 'category', label: 'Category' },
          { key: 'total', label: 'Total', money: true, render: (r) => fmt(r.total) },
          { key: 'status', label: 'Status', render: (r) => (r.void ? 'Void' : r.status) },
        ]}
        onRowClick={(r) => go('form', 'txndetail', { txnId: r.txn_id })}
        rowActions={(r) => [
          { label: 'View/Edit', onClick: () => go('form', 'txndetail', { txnId: r.txn_id }) },
          { label: 'Copy', onClick: () => go('form', 'txndetail', { txnId: r.txn_id, copy: true }) },
          { label: 'Void', danger: true, onClick: () => doVoid(r) },
        ]} />
    </div>
  );
}

// ── (E3) VENDORS ────────────────────────────────────────────────────────────────
export function VendorsPage({ base, go }) {
  const [vendors, setVendors] = useState([]);
  const [bar, setBar] = useState(null);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState({});
  const [includeInactive, setIncludeInactive] = useState(true);
  const load = () => axios.get(`${base}/vendors?scope=all`).then((r) => setVendors(r.data || [])).catch(() => setVendors([]));
  useEffect(() => { load(); axios.get(`${base}/accounting/vendor-summary`).then((r) => setBar(r.data)).catch(() => setBar(null)); }, [base]);
  const ob = (r) => (r.open_balance != null ? r.open_balance : (r.balance || 0));
  const rows = vendors.filter((v) => (includeInactive || v.is_active !== false) && (!q || String(v.name || '').toLowerCase().includes(q.toLowerCase())));
  const makeInactive = async (ids) => { for (const id of ids) await axios.put(`${base}/vendors/${id}`, { is_active: false }).catch(() => {}); setSel({}); load(); };
  return (
    <div className="space-y-3 text-slate-800" data-testid="vendors-page">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xl font-semibold">Vendors</h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => go('form', 'paybills')} data-testid="vendors-pay">Pay vendors</Button>
          <SplitButton label="New vendor" testid="vendors-new" onPrimary={() => go('form', 'vendor')}
            items={[{ label: 'Add vendor', onClick: () => go('form', 'vendor') }, { label: 'Create bill', onClick: () => go('form', 'bill') }, { label: 'Create expense', onClick: () => go('form', 'expense') }]} />
        </div>
      </div>
      {bar && (
        <div className="grid md:grid-cols-2 gap-3" data-testid="vendors-money-bar">
          <div className={`rounded-lg border p-3 ${CARD}`}>
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Unpaid · Last 365 Days</div>
            <div className="grid grid-cols-2 gap-3">
              <div data-testid="vendors-money-bar-overdue"><div className="text-xs text-slate-500">Overdue</div><div className="text-lg font-semibold tabular-nums">{fmt(bar.overdue.amount)}</div><div className="text-xs text-slate-400">{bar.overdue.count} bills</div></div>
              <div data-testid="vendors-money-bar-open"><div className="text-xs text-slate-500">Open bills</div><div className="text-lg font-semibold tabular-nums">{fmt(bar.open_bills.amount)}</div><div className="text-xs text-slate-400">{bar.open_bills.count} bills</div></div>
            </div>
          </div>
          <div className={`rounded-lg border p-3 ${CARD}`}>
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Paid</div>
            <div data-testid="vendors-money-bar-paid30"><div className="text-xs text-slate-500">Paid last 30 days</div><div className="text-lg font-semibold tabular-nums">{fmt(bar.paid_30.amount)}</div><div className="text-xs text-slate-400">bill payments</div></div>
          </div>
        </div>
      )}
      <Toolbar>
        <SearchBox value={q} onChange={setQ} testid="vendors-search" />
        <label className="flex items-center gap-1.5 text-sm text-slate-600"><input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} data-testid="vendors-include-inactive" />Include inactive</label>
        {Object.values(sel).some(Boolean) && <Button variant="outline" onClick={() => makeInactive(Object.keys(sel).filter((k) => sel[k]))} data-testid="vendors-batch-inactive">Make inactive ({Object.values(sel).filter(Boolean).length})</Button>}
      </Toolbar>
      <FeedTable view="vendors-list" rows={rows} selectable sel={sel} setSel={setSel} rowKey={(r) => r.name_id || r.vendor_id}
        empty="No vendors match." defaultSort={{ by: 'name', dir: 'asc' }}
        columns={[
          { key: 'name', label: 'Vendor', render: (r) => r.name },
          { key: 'company', label: 'Company name', render: (r) => r.company || '' },
          { key: 'phone', label: 'Phone' }, { key: 'email', label: 'Email' },
          { key: 'is_1099', label: '1099 tracking', sortVal: (r) => (r.is_1099 ? 1 : 0), render: (r) => (r.is_1099 ? 'Tracked' : '') },
          { key: 'open_balance', label: 'Open balance', money: true, sortVal: (r) => ob(r), csv: (r) => (ob(r) / 100).toFixed(2), render: (r) => fmt(ob(r)) },
        ]}
        rowActions={(r) => [
          { label: 'Create bill', onClick: () => go('form', 'bill') },
          { label: 'Create expense', onClick: () => go('form', 'expense') },
          { label: 'Write check', onClick: () => go('form', 'check') },
          { label: 'Make inactive', danger: true, onClick: () => makeInactive([r.name_id || r.vendor_id]) },
        ]} />
    </div>
  );
}

// ── (E4) BILLS (For review / Unpaid / Paid / Recurring) ────────────────────────
export function BillsPage({ base, go }) {
  const [tab, setTab] = useState('unpaid');
  const [data, setData] = useState({ rows: [] });
  const [recurring, setRecurring] = useState([]);
  const [q, setQ] = useState('');
  const [vendorF, setVendorF] = useState('All');
  const df = useDateFilter('this_year');
  const load = () => { if (tab === 'unpaid' || tab === 'paid') axios.get(`${base}/accounting/bills?status=${tab}`).then((r) => setData(r.data || { rows: [] })).catch(() => setData({ rows: [] })); };
  useEffect(() => { load(); }, [base, tab]);
  useEffect(() => { if (tab === 'recurring') axios.get(`${base}/recurring`).then((r) => setRecurring((r.data?.rows || []).filter((t) => String(t.txn_type || '').toLowerCase().includes('bill')))).catch(() => setRecurring([])); }, [base, tab]);
  const allRows = data.rows || [];
  const vendors = useMemo(() => ['All', ...Array.from(new Set(allRows.map((r) => r.payee).filter(Boolean)))], [allRows]);
  const rows = allRows.filter((r) => (vendorF === 'All' || r.payee === vendorF) && df.inRange(r.date)
    && (!q || [r.payee, String(r.num)].some((x) => String(x || '').toLowerCase().includes(q.toLowerCase()))));
  const TABS = [['review', 'For review'], ['unpaid', 'Unpaid'], ['paid', 'Paid'], ['recurring', 'Recurring']];
  return (
    <div className="space-y-3 text-slate-800" data-testid="bills-page">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xl font-semibold">Bills</h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => go('form', 'paybills')} data-testid="bills-pay">Pay bills</Button>
          <SplitButton label="Add bill" testid="bills-new" onPrimary={() => go('form', 'bill')}
            items={[{ label: 'Add bill', onClick: () => go('form', 'bill') }, { label: 'Upload files', onClick: () => go('receipts') }]} />
        </div>
      </div>
      <div className="flex items-center gap-1 border-b border-slate-200">
        {TABS.map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} className={`px-4 py-2 text-sm -mb-px border-b-2 ${tab === v ? 'border-emerald-600 text-emerald-700 font-medium' : 'border-transparent text-slate-500'}`} data-testid={`bills-tab-${v}`}>{l}</button>
        ))}
      </div>
      {tab === 'review' && <div className="rounded-lg border p-6 text-center text-slate-500 bg-white" data-testid="bills-review-empty">No bills found — Remove filters to find bills or select Upload files to create bills automatically.</div>}
      {tab === 'recurring' && (
        <FeedTable view="bills-recurring" rows={recurring} rowKey={(r) => r.recurring_id}
          empty="No recurring bills."
          columns={[{ key: 'name', label: 'Template name' }, { key: 'party', label: 'Vendor' }, { key: 'interval', label: 'Interval' }, { key: 'next_date', label: 'Next date', render: (r) => fmtDate(r.next_date) }, { key: 'amount', label: 'Amount', money: true, render: (r) => fmt(r.amount) }]}
          rowActions={(r) => [{ label: 'Manage', onClick: () => go('recurring') }]} />
      )}
      {(tab === 'unpaid' || tab === 'paid') && (
        <>
          <Toolbar>
            <Sel value={vendorF} onChange={setVendorF} options={vendors.map((v) => [v, v === 'All' ? 'Vendor: All' : v])} testid="bills-vendor" />
            {df.control}
            <SearchBox value={q} onChange={setQ} testid="bills-search" placeholder="Filters — payee or no." />
          </Toolbar>
          <FeedTable view="bills-list" rows={rows} rowKey={(r) => r.txn_id}
            empty="No bills found — Remove filters to find bills or select Upload files to create bills automatically."
            onRowClick={(r) => go('form', 'txndetail', { txnId: r.txn_id })}
            columns={[
              { key: 'payee', label: 'Vendor' },
              { key: 'due_date', label: 'Due date', render: (r) => fmtDate(r.due_date) },
              { key: 'bill_amount', label: 'Bill amount', money: true, render: (r) => fmt(r.bill_amount) },
              { key: 'open_balance', label: 'Open balance', money: true, render: (r) => fmt(r.open_balance) },
              { key: 'status', label: 'Status' },
            ]}
            rowActions={(r) => (tab === 'unpaid'
              ? [{ label: 'View/Edit', onClick: () => go('form', 'txndetail', { txnId: r.txn_id }) }, { label: 'Schedule payment', onClick: () => go('form', 'paybills') }]
              : [{ label: 'View/Edit', onClick: () => go('form', 'txndetail', { txnId: r.txn_id }) }])} />
        </>
      )}
    </div>
  );
}

// ── BILL PAYMENTS (unchanged feature; kept for the Expenses tab) ───────────────
export function BillPaymentsPage({ base, go }) {
  const [data, setData] = useState({ rows: [] });
  const [q, setQ] = useState('');
  useEffect(() => { axios.get(`${base}/accounting/bill-payments`).then((r) => setData(r.data || { rows: [] })).catch(() => setData({ rows: [] })); }, [base]);
  const rows = (data.rows || []).filter((r) => !q || [r.payee, String(r.num)].some((x) => String(x || '').toLowerCase().includes(q.toLowerCase())));
  return (
    <div className="space-y-3 text-slate-800" data-testid="bill-payments-page">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xl font-semibold">Bill payments</h2>
        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => go('form', 'paybills')} data-testid="bill-payments-new">Pay bills</Button>
      </div>
      <Toolbar><SearchBox value={q} onChange={setQ} testid="bill-payments-search" placeholder="Search payee or no." /></Toolbar>
      <FeedTable view="bill-payments-list" rows={rows} rowKey={(r) => r.txn_id}
        empty="No bill payments yet." onRowClick={(r) => go('form', 'txndetail', { txnId: r.txn_id })}
        columns={[
          { key: 'date', label: 'Date', render: (r) => fmtDate(r.date) }, { key: 'num', label: 'No.' },
          { key: 'payee', label: 'Payee' }, { key: 'method', label: 'Method' },
          { key: 'amount', label: 'Amount', money: true, render: (r) => fmt(r.amount) },
        ]}
        rowActions={(r) => [{ label: 'View/Edit', onClick: () => go('form', 'txndetail', { txnId: r.txn_id }) }]} />
    </div>
  );
}

// ── (S2) SALES TRANSACTIONS ────────────────────────────────────────────────────
export function SalesTransactionsPage({ base, go }) {
  const [data, setData] = useState({ rows: [] });
  const [bar365, setBar365] = useState(null);
  const [q, setQ] = useState('');
  const [typeF, setTypeF] = useState('All');
  const df = useDateFilter('last_3_months');
  useEffect(() => {
    axios.get(`${base}/accounting/sales-transactions`).then((r) => setData(r.data || { rows: [] })).catch(() => setData({ rows: [] }));
    axios.get(`${base}/accounting/sales-bar-365`).then((r) => setBar365(r.data)).catch(() => setBar365(null));
  }, [base]);
  const rows = data.rows || [];
  const types = useMemo(() => ['All', ...Array.from(new Set(rows.map((r) => r.type)))], [rows]);
  const filtered = rows.filter((r) => (typeF === 'All' || r.type === typeF) && df.inRange(r.date)
    && (!q || [r.customer, r.memo, String(r.num), r.type].some((x) => String(x || '').toLowerCase().includes(q.toLowerCase()))));
  return (
    <div className="space-y-3 text-slate-800" data-testid="sales-txns-page">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xl font-semibold">Sales transactions</h2>
        <SplitButton label="Create invoice" testid="sales-txns-new" onPrimary={() => go('form', 'invoice')}
          items={[{ label: 'Invoice', onClick: () => go('form', 'invoice') }, { label: 'Sales receipt', onClick: () => go('form', 'salesreceipt') }, { label: 'Receive payment', onClick: () => go('form', 'payment') }]} />
      </div>
      {bar365 && <MoneyBar5 testid="sales-money-bar" segments={[
        { key: 'estimates', label: 'Estimates', amount: bar365.estimates.amount },
        { key: 'unbilled', label: 'Unbilled income', amount: bar365.unbilled.amount },
        { key: 'overdue', label: 'Overdue', amount: bar365.overdue.amount, sub: `${bar365.overdue.count} open` },
        { key: 'open', label: 'Open invoice or credit', amount: bar365.open.amount, sub: `${bar365.open.count} open` },
        { key: 'paid', label: 'Recently paid', amount: bar365.recently_paid.amount, sub: 'last 365 days' },
      ]} />}
      <Toolbar>
        <SearchBox value={q} onChange={setQ} testid="sales-txns-search" placeholder="Search customer, memo, no." />
        <Sel value={typeF} onChange={setTypeF} options={types.map((t) => [t, t])} testid="sales-txns-type" />
        {df.control}
      </Toolbar>
      <FeedTable view="sales-txns" rows={filtered} rowKey={(r) => r.txn_id}
        empty="No matching sales transactions found." onRowClick={(r) => go('form', 'txndetail', { txnId: r.txn_id })}
        columns={[
          { key: 'date', label: 'Date', render: (r) => fmtDate(r.date) }, { key: 'type', label: 'Type' },
          { key: 'num', label: 'No.' }, { key: 'customer', label: 'Customer' }, { key: 'memo', label: 'Memo' },
          { key: 'amount', label: 'Amount', money: true, render: (r) => fmt(r.amount) },
          { key: 'status', label: 'Status', render: (r) => (r.void ? 'Void' : r.status) },
        ]}
        rowActions={(r) => [
          { label: 'View/Edit', onClick: () => go('form', 'txndetail', { txnId: r.txn_id }) },
          ...(r.code === 'INV' ? [{ label: 'Receive payment', onClick: () => go('form', 'payment', { customerId: r.customer_id }) }] : []),
          { label: 'Copy', onClick: () => go('form', 'txndetail', { txnId: r.txn_id, copy: true }) },
        ]} />
    </div>
  );
}

// ── (S3) INVOICES (two bars) ───────────────────────────────────────────────────
export function InvoicesPage({ base, go, isNonprofit }) {
  const [data, setData] = useState({ rows: [] });
  const [q, setQ] = useState('');
  const [statusF, setStatusF] = useState('All');
  const df = useDateFilter('last_3_months');
  useEffect(() => { axios.get(`${base}/accounting/invoices`).then((r) => setData(r.data || { rows: [] })).catch(() => setData({ rows: [] })); }, [base]);
  const rows = data.rows || [];
  const bar = data.money_bar;
  const noun = isNonprofit ? 'pledge' : 'invoice';
  const statuses = useMemo(() => ['All', ...Array.from(new Set(rows.map((r) => r.status).filter(Boolean)))], [rows]);
  const filtered = rows.filter((r) => (statusF === 'All' || r.status === statusF) && df.inRange(r.date)
    && (!q || [r.customer, String(r.num)].some((x) => String(x || '').toLowerCase().includes(q.toLowerCase()))));
  return (
    <div className="space-y-3 text-slate-800" data-testid="invoices-page">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xl font-semibold">Invoices</h2>
        <SplitButton label={`Create ${noun}`} testid="invoices-new" onPrimary={() => go('form', 'invoice')}
          items={[{ label: `Create ${noun}`, onClick: () => go('form', 'invoice') }, { label: 'Receive payment', onClick: () => go('form', 'payment') }]} />
      </div>
      {bar && (
        <div className="grid md:grid-cols-2 gap-3" data-testid="invoices-money-bar">
          <div className={`rounded-lg border p-3 ${CARD}`}>
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">{fmt(bar.overdue.amount + bar.open_invoices.amount)} Unpaid · Last 365 days</div>
            <div className="grid grid-cols-2 gap-3">
              <div data-testid="invoices-money-bar-overdue"><div className="text-xs text-slate-500">Overdue</div><div className="text-lg font-semibold tabular-nums">{fmt(bar.overdue.amount)}</div><div className="text-xs text-slate-400">{bar.overdue.count} {noun}s</div></div>
              <div data-testid="invoices-money-bar-notdue"><div className="text-xs text-slate-500">Not due yet</div><div className="text-lg font-semibold tabular-nums">{fmt(Math.max(0, bar.open_invoices.amount - bar.overdue.amount))}</div><div className="text-xs text-slate-400">open</div></div>
            </div>
          </div>
          <div className={`rounded-lg border p-3 ${CARD}`}>
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">{fmt(bar.recently_paid.amount)} Paid · Last 30 days</div>
            <div className="grid grid-cols-2 gap-3">
              <div data-testid="invoices-money-bar-notdeposited"><div className="text-xs text-slate-500">Not deposited</div><div className="text-lg font-semibold tabular-nums">{fmt(Math.max(0, bar.recently_paid.amount - bar.deposited.amount))}</div></div>
              <div data-testid="invoices-money-bar-deposited"><div className="text-xs text-slate-500">Deposited</div><div className="text-lg font-semibold tabular-nums">{fmt(bar.deposited.amount)}</div></div>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center gap-1 border-b border-slate-200">
        <button className="px-4 py-2 text-sm -mb-px border-b-2 border-emerald-600 text-emerald-700 font-medium" data-testid="invoices-tab-all">All {noun}s</button>
      </div>
      <Toolbar>
        <SearchBox value={q} onChange={setQ} testid="invoices-search" placeholder="Search customer or no." />
        <Sel value={statusF} onChange={setStatusF} options={statuses.map((s) => [s, s])} testid="invoices-status" />
        {df.control}
        <button type="button" disabled={!filtered.length} data-testid="invoices-print-batch" title={`Print the ${filtered.length} ${noun}s shown, one per page`}
          className="h-9 rounded border border-slate-300 px-3 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          onClick={async () => {
            const r = await axios.post(`${base}/documents/print`, { txn_ids: filtered.slice(0, 500).map((x) => x.txn_id) }, { responseType: 'blob' });
            window.open(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank');
          }}>Print {Math.min(filtered.length, 500)} shown</button>
      </Toolbar>
      <FeedTable view="invoices-list" rows={filtered} rowKey={(r) => r.txn_id}
        empty={`No ${noun}s match.`} onRowClick={(r) => go('form', 'txndetail', { txnId: r.txn_id })}
        columns={[
          { key: 'date', label: 'Date', render: (r) => fmtDate(r.date) }, { key: 'num', label: 'No.' },
          { key: 'customer', label: 'Customer' },
          { key: 'amount', label: 'Amount', money: true, render: (r) => fmt(r.amount) },
          { key: 'status', label: 'Status' },
        ]}
        rowActions={(r) => [
          { label: 'View/Edit', onClick: () => go('form', 'txndetail', { txnId: r.txn_id }) },
          { label: 'Receive payment', onClick: () => go('form', 'payment', { customerId: r.customer_id }) },
        ]} />
    </div>
  );
}

// ── (S4) PRODUCTS & SERVICES ───────────────────────────────────────────────────
export function ProductsPage({ base, go }) {
  const [items, setItems] = useState([]);
  const [accts, setAccts] = useState([]);
  const [q, setQ] = useState('');
  const [typeF, setTypeF] = useState('All');
  const [statusF, setStatusF] = useState('All');
  const [sel, setSel] = useState({});
  const load = () => axios.get(`${base}/lists/items`).then((r) => setItems(r.data || [])).catch(() => setItems([]));
  useEffect(() => { load(); axios.get(`${base}/accounts`).then((r) => setAccts(r.data?.accounts || [])).catch(() => {}); }, [base]);
  const acctName = useMemo(() => { const m = {}; accts.forEach((a) => { m[a.account_id] = a.full_name || a.name; }); return m; }, [accts]);
  const types = useMemo(() => ['All', ...Array.from(new Set(items.map((it) => it.type || 'Service')))], [items]);
  const rows = items.filter((it) => (statusF === 'All' || (statusF === 'Active' ? it.is_active !== false : it.is_active === false))
    && (typeF === 'All' || (it.type || 'Service') === typeF)
    && (!q || [it.name, it.sku, it.description].some((x) => String(x || '').toLowerCase().includes(q.toLowerCase()))));
  const makeInactive = async (ids) => { for (const id of ids) await axios.put(`${base}/lists/items/${id}`, { is_active: false }).catch(() => {}); setSel({}); load(); };
  const [more, setMore] = useState(false);
  return (
    <div className="space-y-3 text-slate-800" data-testid="products-page">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xl font-semibold">Products &amp; services</h2>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Button variant="outline" onClick={() => setMore((v) => !v)} data-testid="products-more">More <ChevronDown className="h-4 w-4 ml-1" /></Button>
            {more && <div className="absolute right-0 top-10 z-30 w-44 rounded-lg border bg-white border-slate-200 shadow-xl" data-testid="products-more-menu">
              <button className="block w-full text-left px-3 py-2 text-sm hover:bg-emerald-600/10" onClick={() => { setMore(false); window.print(); }}>Print list</button>
              <button className="block w-full text-left px-3 py-2 text-sm hover:bg-emerald-600/10" onClick={() => { setMore(false); go('reports'); }}>Run report</button>
            </div>}
          </div>
          <SplitButton label="New product/service" testid="products-new" onPrimary={() => go('form', 'item')}
            items={[{ label: 'Service', onClick: () => go('form', 'item') }, { label: 'Non-inventory', onClick: () => go('form', 'item') }]} />
        </div>
      </div>
      <Toolbar>
        <SearchBox value={q} onChange={setQ} testid="products-search" placeholder="Search name, SKU" />
        <Sel value={typeF} onChange={setTypeF} options={types.map((t) => [t, t === 'All' ? 'Type: All' : t])} testid="products-type" />
        <Sel value={statusF} onChange={setStatusF} options={[['All', 'Status: All'], ['Active', 'Active'], ['Inactive', 'Inactive']]} testid="products-status" />
        {Object.values(sel).some(Boolean) && <Button variant="outline" onClick={() => makeInactive(Object.keys(sel).filter((k) => sel[k]))} data-testid="products-batch-inactive">Make inactive ({Object.values(sel).filter(Boolean).length})</Button>}
      </Toolbar>
      <FeedTable view="products-list" rows={rows} selectable sel={sel} setSel={setSel} rowKey={(r) => r.item_id}
        empty="No products or services match." defaultSort={{ by: 'name', dir: 'asc' }}
        columns={[
          { key: 'name', label: 'Name', render: (r) => <span>{r.name}{r.is_active === false ? ' (inactive)' : ''}</span> },
          { key: 'description', label: 'Sales description', render: (r) => r.description || '' },
          { key: 'category', label: 'Category', render: (r) => r.category || 'Uncategorized' },
          { key: 'sku', label: 'SKU', render: (r) => r.sku || '' },
          { key: 'type', label: 'Type', render: (r) => r.type || 'Service' },
          { key: 'price', label: 'Price', money: true, render: (r) => (r.price == null ? '' : fmt(r.price)) },
          { key: 'cost', label: 'Cost', money: true, render: (r) => (r.cost == null ? '' : fmt(r.cost)) },
        ]}
        rowActions={(r) => [
          { label: 'Edit', onClick: () => go('form', 'item') },
          { label: 'Run report', onClick: () => go('reports') },
          { label: 'Duplicate', onClick: () => go('form', 'item') },
          { label: 'Make inactive', danger: true, onClick: () => makeInactive([r.item_id]) },
        ]} />
    </div>
  );
}

// ── (C1) CUSTOMERS & LEADS ──────────────────────────────────────────────────────
export function CustomersLeadsPage({ base, go, isNonprofit }) {
  const [custs, setCusts] = useState([]);
  const [bar365, setBar365] = useState(null);
  const [tab, setTab] = useState('customers');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState({});
  const [includeInactive, setIncludeInactive] = useState(true);
  const load = () => axios.get(`${base}/customers?scope=all`).then((r) => setCusts(r.data || [])).catch(() => setCusts([]));
  useEffect(() => { load(); axios.get(`${base}/accounting/sales-bar-365`).then((r) => setBar365(r.data)).catch(() => setBar365(null)); }, [base]);
  const noun = isNonprofit ? 'pledge' : 'invoice';
  const ob = (r) => (r.open_balance != null ? r.open_balance : (r.balance || 0));
  const isLead = (c) => !!c.is_lead;
  const rows = custs.filter((c) => (tab === 'leads' ? isLead(c) : !isLead(c)) && (includeInactive || c.is_active !== false)
    && (!q || String(c.display_name || c.name || '').toLowerCase().includes(q.toLowerCase())));
  const makeInactive = async (ids) => { for (const id of ids) await axios.put(`${base}/names/${id}`, { is_active: false }).catch(() => {}); setSel({}); load(); };
  return (
    <div className="space-y-3 text-slate-800" data-testid="customers-leads-page">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xl font-semibold">Customers &amp; leads</h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => go('customerhub_overview')} data-testid="customers-requests">Customer requests</Button>
          <SplitButton label="New customer" testid="customers-new" onPrimary={() => go('form', 'customer')}
            items={[{ label: 'Add customer', onClick: () => go('form', 'customer') }, { label: `Create ${noun}`, onClick: () => go('form', 'invoice') }]} />
        </div>
      </div>
      {bar365 && <MoneyBar5 testid="customers-money-bar" segments={[
        { key: 'estimates', label: 'Estimates', amount: bar365.estimates.amount },
        { key: 'unbilled', label: 'Unbilled income', amount: bar365.unbilled.amount },
        { key: 'overdue', label: 'Overdue', amount: bar365.overdue.amount, sub: `${bar365.overdue.count} open` },
        { key: 'open', label: 'Open invoice or credit', amount: bar365.open.amount, sub: `${bar365.open.count} open` },
        { key: 'paid', label: 'Recently paid', amount: bar365.recently_paid.amount, sub: 'last 365 days' },
      ]} />}
      <div className="flex items-center gap-1 border-b border-slate-200">
        {[['customers', 'Customers'], ['leads', 'Leads']].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} className={`px-4 py-2 text-sm -mb-px border-b-2 ${tab === v ? 'border-emerald-600 text-emerald-700 font-medium' : 'border-transparent text-slate-500'}`} data-testid={`customers-tab-${v}`}>{l}</button>
        ))}
      </div>
      <Toolbar>
        <SearchBox value={q} onChange={setQ} testid="customers-search" />
        <label className="flex items-center gap-1.5 text-sm text-slate-600"><input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} data-testid="customers-include-inactive" />Include inactive</label>
        {Object.values(sel).some(Boolean) && <Button variant="outline" onClick={() => makeInactive(Object.keys(sel).filter((k) => sel[k]))} data-testid="customers-batch-inactive">Make inactive ({Object.values(sel).filter(Boolean).length})</Button>}
      </Toolbar>
      <FeedTable view="customers-list" rows={rows} selectable sel={sel} setSel={setSel} rowKey={(r) => r.name_id}
        empty={tab === 'leads' ? 'No leads yet.' : 'No customers match.'} defaultSort={{ by: 'name', dir: 'asc' }}
        columns={[
          { key: 'name', label: 'Name', sortVal: (r) => (r.display_name || r.name || ''), render: (r) => r.display_name || r.name },
          { key: 'company', label: 'Company name', render: (r) => r.company || '' },
          { key: 'phone', label: 'Phone' },
          { key: 'open_balance', label: 'Open balance', money: true, sortVal: (r) => ob(r), csv: (r) => (ob(r) / 100).toFixed(2), render: (r) => fmt(ob(r)) },
        ]}
        rowActions={(r) => [
          { label: `Create ${noun}`, onClick: () => go('form', 'invoice') },
          { label: 'Receive payment', onClick: () => go('form', 'payment') },
          { label: 'Make inactive', danger: true, onClick: () => makeInactive([r.name_id]) },
        ]} />
    </div>
  );
}
